import { TenantPaymentMethodSchema } from '@repo/tenancy/types';
import { type SelectFieldDataItem } from '@repo/ui/select';
import { sortByPriorities } from '@repo/utils/sort';
import { z } from 'zod';
import {
  CREDIT_CARD_CURRENCY_PRIORITIES,
  CREDIT_CARD_PROVIDER_PRIORITIES,
  CRYPTO_CURRENCY_PRIORITIES,
  CRYPTO_PROVIDER_PRIORITIES,
  NETWORK_PRIORITIES,
  OTHER_CRYPTO_CURRENCY_PRIORITIES,
} from '@/hooks/usePaymentMethodsWithNetworks/constant';
import { type CryptoAddressResponseType, type PaymentResponseType } from '@/lib/fetch';

export type MethodOption = {
  providerId: string;
  name: string;
  paymentType: 'CRYPTO' | 'CREDIT_CARD';
  currencyCode: string;
  minimumAmount: number | null;
  maximumAmount: number | null;
};

export type DepositPaymentOptions = {
  topCryptoOptions: MethodOption[];
  otherCryptoOptions: MethodOption[];
  creditCardOptions: MethodOption[];
};

export type ActiveMethods = {
  hasCrypto: boolean;
  hasCryptoMoonpay: boolean;
  hasFiat: boolean;
};

export interface NetworkOption extends SelectFieldDataItem {
  walletAddress: string;
}

export function buildNetworkOptions(
  walletData: CryptoAddressResponseType[] | undefined,
  currencyCode: string
): NetworkOption[] {
  if (!walletData) return [];
  const record = walletData.find((r) => r.currency === currencyCode);
  if (!record || !record.networks) return [];

  const valid = record.networks.filter((n): n is { network: string; walletAddress: string } =>
    Boolean(n.network && n.walletAddress)
  );

  const mapped: NetworkOption[] = valid.map((n) => ({
    label: n.network,
    value: n.network,
    walletAddress: n.walletAddress,
  }));

  return sortByPriorities(mapped, (opt) => opt.value, NETWORK_PRIORITIES);
}

export function getPaymentOptions(data: PaymentResponseType[] | undefined, topNCrypto = 5): DepositPaymentOptions {
  if (!data) {
    return {
      topCryptoOptions: [],
      otherCryptoOptions: [],
      creditCardOptions: [],
    };
  }

  // 1️⃣ Flatten to MethodOption[]
  const allOptions: MethodOption[] = data.flatMap((gw) => {
    const providerId = gw.providerId ?? '';
    const name = gw.name ?? '';
    const paymentType = gw.paymentType === 'CRYPTO' ? 'CRYPTO' : 'CREDIT_CARD';

    return (gw.currencies ?? [])
      .filter((c): c is { code: string; minimumAmount?: number; maximumAmount?: number } => typeof c.code === 'string')
      .map((c) => ({
        providerId,
        name,
        paymentType,
        currencyCode: c.code,
        minimumAmount: c.minimumAmount ?? null,
        maximumAmount: c.maximumAmount ?? null,
      }));
  });

  // 2️⃣ Crypto: sort by provider, then currency, dedupe
  let cryptos = allOptions.filter((o) => o.paymentType === 'CRYPTO');
  cryptos = sortByPriorities(cryptos, (o) => o.name, CRYPTO_PROVIDER_PRIORITIES);
  cryptos = sortByPriorities(cryptos, (o) => o.currencyCode, CRYPTO_CURRENCY_PRIORITIES);

  const seen = new Set<string>();
  const uniqueCryptos = cryptos.filter((o) => {
    if (seen.has(o.currencyCode)) return false;
    seen.add(o.currencyCode);
    return true;
  });

  // 3️⃣ Split top vs other
  const topCryptoOptions = uniqueCryptos.slice(0, topNCrypto);
  let otherCryptoOptions = uniqueCryptos.slice(topNCrypto);

  // 4️⃣ Exclude chain-specific if base exists
  otherCryptoOptions = otherCryptoOptions.filter((o) => {
    if (!o.currencyCode.includes('_')) return true;
    const [base] = o.currencyCode.split('_');
    return !uniqueCryptos.some((x) => x.currencyCode === base);
  });

  // 5️⃣ Fallback sort for “other”
  otherCryptoOptions = sortByPriorities(
    otherCryptoOptions,
    (o) => o.currencyCode,
    OTHER_CRYPTO_CURRENCY_PRIORITIES,
    CRYPTO_CURRENCY_PRIORITIES
  );

  // 6️⃣ Credit cards: sort by provider, then currency
  let creditCardOptions = allOptions.filter((o) => o.paymentType === 'CREDIT_CARD');
  creditCardOptions = sortByPriorities(creditCardOptions, (o) => o.name, CREDIT_CARD_PROVIDER_PRIORITIES);
  creditCardOptions = sortByPriorities(creditCardOptions, (o) => o.currencyCode, CREDIT_CARD_CURRENCY_PRIORITIES);
  return { topCryptoOptions, otherCryptoOptions, creditCardOptions };
}

export const getActiveMethods = (confPayments: z.infer<typeof TenantPaymentMethodSchema>[]) =>
  confPayments.reduce<ActiveMethods>(
    (methods, confPayment) => {
      if (!confPayment) return methods;
      const { type, gateway, status } = confPayment;
      if (status !== 'ACTIVE') return methods;

      if (type === 'CRYPTO') {
        if (gateway === 'MITHRA') methods.hasCrypto = true;
        if (gateway === 'MOONPAY') methods.hasCryptoMoonpay = true;
      }
      if (type === 'FIAT') {
        methods.hasFiat = true;
      }

      return methods;
    },
    { hasCrypto: false, hasCryptoMoonpay: false, hasFiat: false }
  );
